import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';
import NotificationBell from './NotificationBell';
import config from '../config';
import './Layout.css';

const SUPER_ADMIN_MENUS = [
  { key: 'sa-dashboard', label: 'Dashboard',       path: '/superadmin',                  icon: '⊞' },
  { key: 'sa-companies', label: 'Companies',        path: '/superadmin/companies',       icon: '🏢' },
  { key: 'sa-plans',     label: 'Plans & Features', path: '/superadmin/plans',           icon: '📦' },
  { key: 'sa-billing',   label: 'Billing',          path: '/superadmin/billing',         icon: '💳' },
  { key: 'sa-users',     label: 'Users',            path: '/superadmin/users',           icon: '👤' },
  { key: 'sa-menus',     label: 'Menu Management',  path: '/superadmin/menus',           icon: '☰' },
  { key: 'sa-whatsapp',  label: 'WhatsApp Config',  path: '/superadmin/whatsapp-config', icon: '💬' },
];

const MENU_ICONS = {
  dashboard: '⊞', enquiries: '✉', customers: '👥', projects: '🏗',
  parties: '🏢', // Parties parent menu
  'inventory-management': '📦', 'manage-inventory': '📦', 'manage-assets': '🛠️', 'inventory-categories': '🏷️', warehouses: '🏢', transfers: '🔄',
  'employee-management': '👥', // Employee Management parent menu
  expenses: '💰', vendors: '🏪', employees: '👤', payroll: '💵',
  reports: '📈', users: '👤', roles: '🔐', menus: '☰',
  'audit-logs': '📜', branches: '🏢', subscription: '💳', timesheets: '⏱',
  'my-timesheets': '📝', 'timesheet-approvals': '✅',
  'supervisor-updates': '🏗', 'my-supervisor-updates': '📋', 'supervisor-update-approvals': '✅',
  calendar: '📅',
  finance: '💼', 'account-heads': '📋', transactions: '💳', 
  'customer-payments': '💵', 'vendor-purchases': '🛒', 'purchase-returns': '↩️', 
  'vendor-payments': '💸', 'finance-reports': '📊', 'cash-bank': '🏦', 'cash-advance': '💵',
  'finance-approvals': '✅', 'finance-approval-settings': '⚙️',
  'manage-projects': '📋', 'project-types': '📑', 'project-consumption': '📉',
  settings: '⚙️', 'company-settings': '🏢', 'email-settings': '📧', 'whatsapp-settings': '💬', 'theme-settings': '🎨',
};

function UserAvatar({ name }) {
  const initials = name
    ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';
  return <div className="sl-avatar">{initials}</div>;
}

function BranchSwitcher({ branchAssignments, currentBranchName, onSwitch }) {
  const [open, setOpen] = useState(false);
  if (!branchAssignments || branchAssignments.length <= 1) return null;
  return (
    <div className="sl-branch-switcher">
      <button className="sl-branch-btn" onClick={() => setOpen(!open)}>
        {currentBranchName || 'Switch Branch'} ▾
      </button>
      {open && (
        <ul className="sl-branch-dropdown">
          {branchAssignments.map(a => (
            <li key={a.branchId}>
              <button
                onClick={() => { onSwitch(a.branchId); setOpen(false); }}
                className={a.branchName === currentBranchName ? 'active' : ''}
              >
                {a.branchName}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MenuItem({ menu, collapsed, isActive, onNavigate, children }) {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = children && children.length > 0;

  const handleClick = (e) => {
    if (hasChildren) {
      e.preventDefault();
      setExpanded(!expanded);
    } else {
      onNavigate();
    }
  };

  return (
    <li className="sl-menu-item">
      <Link
        to={menu.path || '#'}
        className={`sl-nav-item ${isActive(menu.path) ? 'sl-active' : ''} ${hasChildren ? 'sl-has-children' : ''}`}
        onClick={handleClick}
        title={collapsed ? menu.label : undefined}
      >
        <span className="sl-nav-icon">
          {MENU_ICONS[menu.key] || menu.icon || '•'}
        </span>
        {!collapsed && (
          <>
            <span className="sl-nav-label">{menu.label}</span>
            {hasChildren && <span className="sl-nav-arrow">{expanded ? '▾' : '▸'}</span>}
          </>
        )}
      </Link>
      {hasChildren && expanded && !collapsed && (
        <ul className="sl-submenu">
          {children.map(child => (
            <li key={child.key}>
              <Link
                to={child.path}
                className={`sl-nav-item sl-nav-subitem ${isActive(child.path) ? 'sl-active' : ''}`}
                onClick={onNavigate}
              >
                <span className="sl-nav-icon">
                  {MENU_ICONS[child.key] || child.icon || '•'}
                </span>
                <span className="sl-nav-label">{child.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Layout() {
  const { logout, user, userMenus, isSuperAdmin, isCompanyAdmin, isAuthenticated, branchName, branchId, branchAssignments, switchBranch } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [branchLogoUrl, setBranchLogoUrl] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);

  // Listen for PWA installation prompt & check standalone / mobile status
  useEffect(() => {
    const isStandaloneMode = (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://')
    );
    setIsStandalone(isStandaloneMode);

    const ua = (window.navigator.userAgent || '').toLowerCase();
    const isIos = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isMobileDevice = /android|iphone|ipad|ipod|windows phone|iemobile|mobile/i.test(ua) || window.innerWidth <= 768;

    setIsIosDevice(isIos);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallBtn(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // If not already in standalone mode, enable install button on mobile devices & iOS
    if (!isStandaloneMode && (isMobileDevice || isIos)) {
      setShowInstallBtn(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowInstallBtn(false);
      }
      setDeferredPrompt(null);
    } else {
      setShowInstallModal(true);
    }
  };

  // Reset scroll to top on navigation/page transition
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Fetch branch logo — match by branchName since branchId may be null for some roles
  useEffect(() => {
    if (isSuperAdmin() || !branchName) return;
    axios.get('/company/branches').then(res => {
      const data = res.data?.data || res.data || [];
      const list = Array.isArray(data) ? data : [];
      // Match by id first, then by name
      const branch = list.find(b => (branchId && (b.id === branchId || String(b.id) === String(branchId))))
                  || list.find(b => b.name === branchName);
      if (branch?.logoUrl) {
        setBranchLogoUrl(`${config.apiBaseUrl}${branch.logoUrl}`);
      }
    }).catch(() => {});
  }, [branchName, branchId]);

  const menus = isSuperAdmin() ? SUPER_ADMIN_MENUS : userMenus;

  const handleSwitchBranch = async (branchId) => {
    const result = await switchBranch(branchId);
    if (result.success) navigate(0);
  };

  const isActive = (path) => {
    if (!path) return false;
    
    // Exact match is always active
    if (location.pathname === path) return true;
    
    // Special cases for root paths
    if (path === '/superadmin') return location.pathname === '/superadmin';
    if (path === '/') return location.pathname === '/';
    
    // Don't highlight parent paths - only exact matches
    // This prevents /inventory from being highlighted when on /inventory/categories
    return false;
  };

  const roleLabel = isSuperAdmin() ? 'Super Admin' : (user?.customRoleName || user?.role?.replace('_', ' ') || '');

  // Build a map of menu.id -> menu for quick lookup
  const menuById = menus.reduce((map, menu) => {
    map[menu.id] = menu;
    return map;
  }, {});

  // Group menus by parent — supports both numeric id-based and key-based parentId
  const menuTree = menus.reduce((acc, menu) => {
    if (!menu.parentId) {
      acc.topLevel.push(menu);
    } else {
      // Store children indexed by the parent's ID (numeric)
      const parentKey = menu.parentId;
      if (!acc.children[parentKey]) {
        acc.children[parentKey] = [];
      }
      acc.children[parentKey].push(menu);
    }
    return acc;
  }, { topLevel: [], children: {} });

  // Helper: get children for a menu (match by id OR by key)
  const getChildren = (menu) => {
    // Children are indexed by the parent's numeric ID in the database
    return menuTree.children[menu.id] || menuTree.children[menu.key] || [];
  };

  return (
    <div className={`sl-layout ${collapsed ? 'sl-collapsed' : ''}`}>

      {/* Sidebar */}
      <nav className={`sl-sidebar ${mobileOpen ? 'sl-mobile-open' : ''}`}>

        {/* Collapse toggle (desktop) */}
        <button className="sl-collapse-btn" onClick={() => setCollapsed(!collapsed)} aria-label="Collapse sidebar">
          {collapsed ? '›' : '‹'}
        </button>

        {/* User profile */}
        <Link to="/profile" className="sl-profile-link">
          <div className="sl-profile">
            <UserAvatar name={user?.name} />
            {!collapsed && (
              <div className="sl-profile-info">
                <span className="sl-profile-role">{roleLabel}</span>
                <span className="sl-profile-name">{user?.name || 'User'}</span>
                {!isSuperAdmin() && !isCompanyAdmin() && branchName && (
                  <span className="sl-profile-branch">{branchName}</span>
                )}
              </div>
            )}
          </div>
        </Link>

        {/* Nav section */}
        {!collapsed && <span className="sl-section-label">MAIN</span>}
        <ul className="sl-nav">
          {menuTree.topLevel.map(menu => (
            <MenuItem
              key={menu.key}
              menu={menu}
              collapsed={collapsed}
              isActive={isActive}
              onNavigate={() => setMobileOpen(false)}
              children={getChildren(menu)}
            />
          ))}
        </ul>

        {/* Branch switcher */}
        {!collapsed && !isSuperAdmin() && !isCompanyAdmin() && (
          <BranchSwitcher
            branchAssignments={branchAssignments}
            currentBranchName={branchName}
            onSwitch={handleSwitchBranch}
          />
        )}
      </nav>

      {mobileOpen && <div className="sl-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Main content */}
      <main className="sl-content">
        <div className="sl-topbar">
          {/* Branch info */}
          {!isSuperAdmin() && branchName && (
            <div className="sl-topbar-branch">
              {branchLogoUrl && (
                <img src={branchLogoUrl} alt={branchName} className="sl-topbar-logo" />
              )}
              <span className="sl-topbar-branch-name">{branchName}</span>
            </div>
          )}

          <div className="sl-topbar-actions">
            {!isStandalone && showInstallBtn && (
              <button
                className="sl-install-pwa-btn"
                onClick={handleInstallClick}
                title="Install Construction ERP App"
              >
                <span className="sl-install-icon">📥</span>
                <span className="sl-install-text-desktop">Install App</span>
                <span className="sl-install-text-mobile">Install</span>
              </button>
            )}
            {!isSuperAdmin() && isAuthenticated() && <NotificationBell />}
            {/* User info + logout */}
            <Link to="/profile" className="sl-topbar-user-link">
              <div className="sl-topbar-user">
                <UserAvatar name={user?.name} />
                <span className="sl-topbar-username">{user?.name}</span>
              </div>
            </Link>
            <button className="sl-topbar-logout" onClick={logout} title="Logout">
              ⏻ Logout
            </button>
          </div>
        </div>
        <Outlet />
      </main>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="sl-bottom-nav">
        <Link
          to={isSuperAdmin() ? '/superadmin' : '/'}
          className={`sl-bottom-item ${location.pathname === '/' || location.pathname === '/superadmin' ? 'active' : ''}`}
        >
          <span className="sl-bottom-icon">⊞</span>
          <span className="sl-bottom-label">Dashboard</span>
        </Link>
        <Link
          to="/projects"
          className={`sl-bottom-item ${location.pathname.startsWith('/projects') ? 'active' : ''}`}
        >
          <span className="sl-bottom-icon">🏗</span>
          <span className="sl-bottom-label">Projects</span>
        </Link>
        <Link
          to="/enquiries"
          className={`sl-bottom-item ${location.pathname.startsWith('/enquiries') ? 'active' : ''}`}
        >
          <span className="sl-bottom-icon">✉</span>
          <span className="sl-bottom-label">Enquiries</span>
        </Link>
        <Link
          to="/timesheets/my"
          className={`sl-bottom-item ${location.pathname.startsWith('/timesheets') ? 'active' : ''}`}
        >
          <span className="sl-bottom-icon">⏱</span>
          <span className="sl-bottom-label">Timesheets</span>
        </Link>
        <button
          type="button"
          className={`sl-bottom-item ${mobileOpen ? 'active' : ''}`}
          onClick={() => setMobileOpen(prev => !prev)}
        >
          <span className="sl-bottom-icon">☰</span>
          <span className="sl-bottom-label">More</span>
        </button>
      </nav>

      {/* PWA Install Instructions Modal for iOS / unsupported browsers */}
      {showInstallModal && (
        <div className="sl-install-modal-backdrop" onClick={() => setShowInstallModal(false)}>
          <div className="sl-install-modal" onClick={e => e.stopPropagation()}>
            <div className="sl-install-modal-header">
              <div className="sl-install-modal-title">
                <span className="sl-install-modal-icon">📲</span>
                <h3>Install Construction ERP</h3>
              </div>
              <button
                type="button"
                className="sl-install-modal-close"
                onClick={() => setShowInstallModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="sl-install-modal-body">
              <p className="sl-install-modal-intro">
                Install this app on your device for quick access, offline capabilities, and a full-screen app experience.
              </p>
              {isIosDevice ? (
                <div className="sl-install-steps">
                  <div className="sl-install-step">
                    <span className="sl-step-num">1</span>
                    <span>Tap the <strong>Share</strong> button <span className="sl-ios-icon">⎋</span> at the bottom of Safari.</span>
                  </div>
                  <div className="sl-install-step">
                    <span className="sl-step-num">2</span>
                    <span>Scroll down and tap <strong>Add to Home Screen</strong> <span className="sl-ios-icon">⊞</span>.</span>
                  </div>
                  <div className="sl-install-step">
                    <span className="sl-step-num">3</span>
                    <span>Tap <strong>Add</strong> in the top-right corner to finish.</span>
                  </div>
                </div>
              ) : (
                <div className="sl-install-steps">
                  <div className="sl-install-step">
                    <span className="sl-step-num">1</span>
                    <span>Tap the <strong>Menu</strong> button (three dots <strong>⋮</strong>) in your browser.</span>
                  </div>
                  <div className="sl-install-step">
                    <span className="sl-step-num">2</span>
                    <span>Select <strong>Install app</strong> or <strong>Add to Home screen</strong>.</span>
                  </div>
                  <div className="sl-install-step">
                    <span className="sl-step-num">3</span>
                    <span>Confirm to install the app on your home screen.</span>
                  </div>
                </div>
              )}
            </div>
            <div className="sl-install-modal-footer">
              <button
                type="button"
                className="sl-install-modal-btn"
                onClick={() => setShowInstallModal(false)}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Layout;
